import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  Upload, CheckCircle, FileText, Download, Trash2, AlertCircle, ShieldCheck, Loader2,
} from 'lucide-react';

const ACCEPTED = '.pdf,.jpg,.jpeg,.png,.webp';
const MAX_SIZE = 12 * 1024 * 1024; // 12 Mo

// One row per required document — handles upload, download, replace and delete.
const DocumentRow = ({ doc, onChanged }) => {
  const [busy, setBusy] = useState(false);

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_SIZE) {
      toast.error(`${file.name} : fichier trop volumineux (max 12 Mo)`);
      return;
    }
    setBusy(true);
    try {
      await api.uploadMyDocument(doc.key, file);
      toast.success(`${doc.label} enregistré`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Téléversement impossible');
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await api.downloadMyDocument(doc.key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || `${doc.key}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Téléchargement impossible');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer ${doc.label} ? Tu devras le redéposer pour valider une candidature.`)) return;
    setBusy(true);
    try {
      await api.deleteMyDocument(doc.key);
      toast.success('Document supprimé');
      onChanged?.();
    } catch (err) {
      toast.error('Suppression impossible');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`rounded-2xl border p-5 md:p-6 transition-all ${
        doc.uploaded
          ? 'bg-[#C9B896]/8 border-[#C9B896]/40'
          : 'bg-white border-[#1F2A4A]/12 hover:border-[#C9B896]/60'
      }`}
      data-testid={`user-doc-row-${doc.key}`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
            doc.uploaded ? 'bg-[#C9B896] text-[#0F1623]' : 'bg-[#FAF7F0] text-[#C9B896]'
          }`}
        >
          {doc.uploaded ? <CheckCircle className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm md:text-base font-medium text-[#1F2A4A] leading-tight">
              {doc.label}
              {doc.required && <span className="text-red-500 ml-1">*</span>}
            </p>
            {doc.uploaded ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] uppercase tracking-wider font-mono">
                <CheckCircle className="w-3 h-3" />
                Déposé
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] uppercase tracking-wider font-mono">
                <AlertCircle className="w-3 h-3" />
                Manquant
              </span>
            )}
          </div>

          {doc.uploaded ? (
            <p className="text-xs text-[#1F2A4A]/65 font-mono mt-1 truncate" title={doc.file_name}>
              {doc.file_name}
            </p>
          ) : (
            <p className="text-xs text-[#1F2A4A]/60 mt-1">
              PDF, JPG ou PNG · max 12 Mo
            </p>
          )}

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <label
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                doc.uploaded
                  ? 'bg-white border border-[#1F2A4A]/15 text-[#1F2A4A] hover:bg-[#FAF7F0]'
                  : 'bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166]'
              } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
              data-testid={`user-doc-${doc.key}-pick`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {doc.uploaded ? 'Remplacer' : 'Téléverser'}
              <input
                type="file"
                accept={ACCEPTED}
                className="hidden"
                onChange={handlePick}
                disabled={busy}
                data-testid={`user-doc-${doc.key}-input`}
              />
            </label>

            {doc.uploaded && (
              <>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-[#1F2A4A]/85 hover:text-[#C9B896] transition-colors"
                  data-testid={`user-doc-${doc.key}-download`}
                >
                  <Download className="w-3.5 h-3.5" />
                  Télécharger
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-[#1F2A4A]/65 hover:text-red-600 transition-colors disabled:opacity-50"
                  data-testid={`user-doc-${doc.key}-delete`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Supprimer
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const UserDocumentsTab = ({ embedded = false }) => {
  const [data, setData] = useState({ documents: [], all_required_uploaded: false, missing_required: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.getMyDocuments();
      setData(res);
    } catch (err) {
      toast.error('Impossible de charger tes documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[#C9B896]" />
      </div>
    );
  }

  const allUploaded = data.all_required_uploaded;

  return (
    <div data-testid="user-documents-tab">
      {/* Header — hidden when embedded inside another section */}
      {!embedded && (
        <div className="mb-6">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">DOCUMENTS ADMINISTRATIFS</p>
          <h2 className="font-light text-3xl md:text-4xl text-[#1F2A4A] tracking-tight">
            Ton <span className="font-serif italic text-[#C9B896]">dossier.</span>
          </h2>
          <p className="text-sm text-[#1F2A4A]/75 mt-3 max-w-2xl">
            Dépose ici les pièces administratives nécessaires à toute inscription.
            Une fois validés, tes documents seront automatiquement attachés à chaque
            nouvelle candidature que tu déposeras.
          </p>
        </div>
      )}

      {/* Status banner — RED when docs missing (blocks any new application) */}
      <div
        className={`rounded-2xl px-5 py-4 mb-5 flex items-start gap-3 border ${
          allUploaded
            ? 'bg-green-50 border-green-200'
            : 'bg-red-50 border-red-300 ring-1 ring-red-200'
        }`}
        data-testid="user-documents-status"
      >
        {allUploaded ? (
          <ShieldCheck className="w-5 h-5 text-green-700 shrink-0 mt-0.5" />
        ) : (
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
        )}
        <div className="text-sm leading-relaxed flex-1">
          {allUploaded ? (
            <>
              <p className="font-medium text-green-800">Ton dossier administratif est complet.</p>
              <p className="text-green-700/85 mt-0.5 text-xs">
                Tu peux désormais déposer une candidature à n'importe quelle session.
              </p>
            </>
          ) : (
            <>
              <p className="font-semibold text-red-700" data-testid="docs-blocking-message">
                Aucune candidature ne peut être soumise tant que tes documents ne sont pas transmis.
              </p>
              <p className="text-red-700/90 mt-1 text-xs">
                Téléverse les pièces obligatoires ci-dessous (Carte d'identité, justificatif de
                domicile, RIB) pour pouvoir déposer un dossier d'inscription.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Document list */}
      <div className="space-y-3">
        {data.documents.map((doc) => (
          <DocumentRow key={doc.key} doc={doc} onChanged={load} />
        ))}
      </div>

      {!embedded && (
        <p className="text-[11px] text-[#1F2A4A]/55 mt-6 leading-relaxed">
          Tes documents sont chiffrés et conservés uniquement pour la constitution
          de ton dossier de formation. Tu peux les remplacer ou les supprimer à
          tout moment.
        </p>
      )}
    </div>
  );
};
